import type { C2C_CLIENT_TEMPLATES } from '@/lib/c2c/copy-declaration'
import type { DeclaredValues } from '@/lib/content-standard/declaration'
import type { TextValue } from '@/lib/content-standard/rich-text'
import type { Clock, Count, Duration, Money } from '@/lib/facts/values'

/** The figures a trip card states, each the text of the copy's template for its line. */
export interface TripCardText {
    /** Absent when the trip cannot be stated in the page's currency. */
    readonly fare?: string
    readonly departs: string
    readonly arrives: string
    readonly duration: string
    /** Absent for a direct trip, which shows no transfer badge. */
    readonly transfers?: string
}

/** The values a card's figures render from. */
export interface TripCardFigures {
    readonly departTime: Clock
    readonly arriveTime: Clock
    readonly duration: Duration
    readonly fare?: Money
    readonly transfers?: Count
}

type Declared = typeof C2C_CLIENT_TEMPLATES

/** Each of the card's templates bound to a formatting decision: `useClientTemplate` in the
 *  island, `serverTemplate` on the server. */
export type TripCardRenders = {
    readonly [Key in keyof Declared]: (values: DeclaredValues<Declared[Key]['values']>) => TextValue
}

export function tripCardText(
    { departTime, arriveTime, duration, fare, transfers }: TripCardFigures,
    render: TripCardRenders,
): TripCardText {
    return {
        ...(fare === undefined ? {} : { fare: render.tripCardFare({ price: fare }).text }),
        departs: render.tripCardClock({ time: departTime }).text,
        arrives: render.tripCardClock({ time: arriveTime }).text,
        duration: render.tripCardDuration({ travelTime: duration }).text,
        ...(transfers !== undefined && transfers > 0
            ? { transfers: render.tripCardTransfers({ transfers }).text }
            : {}),
    }
}
